import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SidebarLayout from '../components/SidebarLayout';
import API from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  Zap, Plus, Phone, MessageCircle, Mail, Search, X, Edit2, Trash2,
  Target, TrendingUp, Calendar, AlertTriangle, User, Building2, Upload, Download, LayoutGrid, List as ListIcon,
  Globe, MapPin, Sparkles, RefreshCw, ArrowRightCircle, FileText  // eslint-disable-line no-unused-vars
} from 'lucide-react';

const STAGES = ['Cold', 'Contacted', 'Meeting', 'Proposal', 'Won', 'Lost'];
const STAGE_COLORS = {
  Cold: 'bg-slate-500/10 text-slate-300 border-slate-500/20',
  Contacted: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20',
  Meeting: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/20',
  Proposal: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  Won: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  Lost: 'bg-rose-500/10 text-rose-300 border-rose-500/20',
};

function fmtINR(n) {
  if (!n) return '—';
  const v = Number(n);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v}`;
}

function cleanPhone(phone) {
  if (!phone) return '';
  return String(phone).replace(/[^\d+]/g, '');
}

function whatsappUrl(phone, text = '') {
  const p = cleanPhone(phone).replace(/^\+/, '');
  return `https://wa.me/${p}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
}

function KPI({ icon, label, value, color }) {
  return (
    <div className={`glass-card p-4 flex items-start justify-between border-${color}-500/10`} data-testid={`bd-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div>
        <p className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-semibold mb-1">{label}</p>
        <p className={`text-2xl font-heading font-black text-${color}-400`}>{value}</p>
      </div>
      <div className="opacity-30">{icon}</div>
    </div>
  );
}

export default function BusinessDevPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const bdLevel = isAdmin ? 'edit' : (user?.effective_permissions?.business_dev || 'none');
  const canWrite = ['task_only', 'edit'].includes(bdLevel) || isAdmin;
  const canDelete = isAdmin || user?.department === "Founder's Office";

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fStage, setFStage] = useState('all');
  const [fOwner, setFOwner] = useState('all');
  const [fSource, setFSource] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [viewLead, setViewLead] = useState(null);
  const [pageView, setPageView] = useState('pipeline'); // 'pipeline' | 'all' | 'daily'
  const [showBulkImport, setShowBulkImport] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fStage && fStage !== 'all') params.stage = fStage;
      if (fOwner && fOwner !== 'all') params.owner_id = fOwner;
      if (fSource && fSource !== 'all') params.source = fSource;
      if (search.trim()) params.search = search.trim();
      const [lr, sr, er] = await Promise.all([
        axios.get(`${API}/business-dev/leads`, { withCredentials: true, params }),
        axios.get(`${API}/business-dev/stats`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }),
      ]);
      setLeads(lr.data);
      setStats(sr.data);
      setEmployees(er.data.filter(e => e.status === 'Active'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fStage, fOwner, fSource, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const groupedByStage = useMemo(() => {
    const map = {};
    STAGES.forEach(s => { map[s] = []; });
    leads.forEach(l => {
      const s = l.stage || 'Cold';
      if (!map[s]) map[s] = [];
      map[s].push(l);
    });
    return map;
  }, [leads]);

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/business-dev/leads/${lead.lead_id}`, { withCredentials: true });
      toast.success('Lead deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading && leads.length === 0) {
    return <SidebarLayout title="Business Development" subtitle="Lead pipeline"><div className="flex items-center justify-center py-20"><div className="text-white">Loading...</div></div></SidebarLayout>;
  }

  return (
    <SidebarLayout title="Business Development" subtitle="Lead Pipeline & Outreach">
      {pageView !== 'daily' && (
        <div className="kpi-scroll md:grid-cols-4 gap-4 mb-4" data-testid="bd-kpis">
          <KPI icon={<Target className="text-purple-400" />} label="Total Leads" value={stats?.total || 0} color="purple" />
          <KPI icon={<TrendingUp className="text-cyan-400" />} label="Pipeline Value" value={fmtINR(stats?.pipeline_value)} color="cyan" />
          <KPI icon={<Zap className="text-emerald-400" />} label="Won Value" value={fmtINR(stats?.won_value)} color="emerald" />
          <KPI icon={<AlertTriangle className="text-rose-400" />} label="Overdue Follow-ups" value={stats?.overdue_followups || 0} color="rose" />
        </div>
      )}

      {/* Filters + New button */}
      <div className="flex flex-wrap gap-2 items-center mb-4 mt-4">
        <div className="inline-flex rounded-lg border border-white/10 bg-black/40 overflow-hidden" data-testid="bd-view-toggle">
          <button onClick={() => setPageView('pipeline')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold transition-colors ${pageView === 'pipeline' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
            data-testid="bd-view-pipeline"><LayoutGrid size={12} /> Pipeline</button>
          <button onClick={() => setPageView('all')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold transition-colors ${pageView === 'all' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
            data-testid="bd-view-all"><ListIcon size={12} /> All Leads</button>
          <button onClick={() => setPageView('daily')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold transition-colors ${pageView === 'daily' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
            data-testid="bd-view-daily"><Sparkles size={12} /> Daily Leads</button>
        </div>
        {pageView !== 'daily' && (<>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name/phone/email"
            className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs pl-8 w-[220px]" data-testid="bd-search" />
        </div>
        <Select value={fStage} onValueChange={setFStage}>
          <SelectTrigger className="w-[130px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="bd-stage-filter"><SelectValue placeholder="Stage" /></SelectTrigger>
          <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fOwner} onValueChange={setFOwner}>
          <SelectTrigger className="w-[150px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="bd-owner-filter"><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">
            <SelectItem value="all">All Owners</SelectItem>
            {employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {canWrite && (
          <Button onClick={() => setShowBulkImport(true)} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5 h-9 text-xs" data-testid="bulk-import-btn">
            <Upload size={14} className="mr-1" /> Bulk Import
          </Button>
        )}
        {canWrite && (
          <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700 h-9 text-xs" data-testid="create-lead-btn">
            <Plus size={14} className="mr-1" /> New Lead
          </Button>
        )}
        </>)}
      </div>

      {pageView === 'pipeline' && (
        /* Kanban-style stage groups */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="bd-stage-board">
          {STAGES.map(stage => (
            <div key={stage} className="glass-card p-3 min-h-[180px]" data-testid={`bd-stage-${stage.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${STAGE_COLORS[stage]}`}>{stage}</div>
                <span className="text-xs text-slate-500">{groupedByStage[stage]?.length || 0}</span>
              </div>
              <div className="space-y-2">
                {(groupedByStage[stage] || []).length === 0 ? (
                  <div className="text-[10px] text-slate-600 text-center py-2">No leads</div>
                ) : groupedByStage[stage].map(lead => (
                  <LeadCard key={lead.lead_id} lead={lead}
                    canWrite={canWrite} canDelete={canDelete}
                    onClick={() => setViewLead(lead)}
                    onEdit={() => setEditingLead(lead)}
                    onDelete={() => handleDelete(lead)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {pageView === 'all' && (
        <AllLeadsTable leads={leads} canWrite={canWrite} canDelete={canDelete}
          onSelect={setViewLead} onEdit={setEditingLead} onDelete={handleDelete} />
      )}
      {pageView === 'daily' && (
        <DailyLeadsTab employees={employees} isAdmin={isAdmin} canWrite={canWrite} onMoved={fetchData} />
      )}

      {showCreate && <LeadDialog mode="create" employees={employees}
        onClose={() => setShowCreate(false)}
        onSaved={() => { setShowCreate(false); fetchData(); }} />}
      {editingLead && <LeadDialog mode="edit" lead={editingLead} employees={employees}
        onClose={() => setEditingLead(null)}
        onSaved={() => { setEditingLead(null); fetchData(); }} />}
      {viewLead && <LeadDetail lead={viewLead}
        onClose={() => setViewLead(null)}
        onChanged={fetchData} canWrite={canWrite} />}
      {showBulkImport && <BulkImportDialog onClose={() => setShowBulkImport(false)} onDone={() => { setShowBulkImport(false); fetchData(); }} />}
    </SidebarLayout>
  );
}

function AllLeadsTable({ leads, canWrite, canDelete, onSelect, onEdit, onDelete }) {
  const today = new Date().toISOString().slice(0, 10);
  if (leads.length === 0) {
    return <div className="glass-card p-12 text-center text-slate-500" data-testid="bd-all-empty">No leads found</div>;
  }
  return (
    <div className="glass-card overflow-hidden" data-testid="bd-all-leads">
      <div className="overflow-x-auto">
        <table className="data-table text-xs">
          <thead><tr>
            <th>Lead ID</th><th>Name</th><th>Contact</th><th>Phone</th><th>Stage</th>
            <th>Source</th><th>Owner</th><th>Follow-up</th><th className="text-right">Value</th><th></th>
          </tr></thead>
          <tbody>
            {leads.map(l => {
              const overdue = l.next_followup && l.next_followup < today && !['Won', 'Lost'].includes(l.stage);
              return (
                <tr key={l.lead_id} className="cursor-pointer hover:bg-white/[0.02]" onClick={() => onSelect(l)} data-testid={`bd-all-row-${l.lead_id}`}>
                  <td className="font-mono text-[10px] text-slate-500">{l.lead_id}</td>
                  <td className="text-white font-semibold max-w-[160px] truncate" title={l.name}>{l.name}</td>
                  <td className="text-slate-300 max-w-[120px] truncate">{l.contact_person || '—'}</td>
                  <td className="text-slate-400 font-mono text-[10px]">{l.phone || '—'}</td>
                  <td><span className={`text-[10px] px-2 py-0.5 rounded border ${STAGE_COLORS[l.stage] || ''}`}>{l.stage}</span></td>
                  <td className="text-slate-400">{l.source || '—'}</td>
                  <td className="text-slate-400">{l.owner_name || '—'}</td>
                  <td className={`font-mono text-[10px] ${overdue ? 'text-rose-400' : 'text-slate-500'}`}>{l.next_followup || '—'}{overdue && ' ⚠'}</td>
                  <td className="text-right font-mono text-emerald-400">{l.potential_value > 0 ? fmtINR(l.potential_value) : '—'}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      {l.phone && (
                        <>
                          <a href={`tel:${cleanPhone(l.phone)}`} className="p-1 rounded bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20" title="Call" data-testid={`bd-all-call-${l.lead_id}`}><Phone size={11} /></a>
                          <a href={whatsappUrl(l.phone, `Hi ${l.contact_person || ''}`.trim())} target="_blank" rel="noopener noreferrer" className="p-1 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20" title="WhatsApp" data-testid={`bd-all-whatsapp-${l.lead_id}`}><MessageCircle size={11} /></a>
                        </>
                      )}
                      {canWrite && <button onClick={() => onEdit(l)} className="p-1 rounded bg-white/5 text-slate-400 hover:text-white" title="Edit" data-testid={`bd-all-edit-${l.lead_id}`}><Edit2 size={11} /></button>}
                      {canDelete && <button onClick={() => onDelete(l)} className="p-1 rounded bg-rose-500/10 text-rose-400" title="Delete" data-testid={`bd-all-delete-${l.lead_id}`}><Trash2 size={11} /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkImportDialog({ onClose, onDone }) {
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const { data } = await axios.get(`${API}/business-dev/csv-template`, { withCredentials: true });
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Template download failed'); }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => { setCsvText(ev.target.result); parseCsv(ev.target.result); };
    reader.readAsText(f);
  };

  const parseCsv = (text) => {
    setParsing(true);
    setParseErrors([]);
    try {
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { setPreview([]); setParseErrors(['File must contain a header row and at least 1 data row']); return; }
      // Simple CSV parser handling quoted fields
      const parseLine = (line) => {
        const out = [];
        let cur = '';
        let inQ = false;
        for (let i = 0; i < line.length; i++) {
          const c = line[i];
          if (c === '"') { inQ = !inQ; continue; }
          if (c === ',' && !inQ) { out.push(cur); cur = ''; continue; }
          cur += c;
        }
        out.push(cur);
        return out;
      };
      const headers = parseLine(lines[0]).map(h => h.trim().toLowerCase());
      const rows = [];
      const errs = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = parseLine(lines[i]);
        const row = {};
        headers.forEach((h, idx) => { row[h] = (vals[idx] || '').trim(); });
        if (!row.name) errs.push(`Row ${i + 1}: missing name`);
        rows.push(row);
      }
      setPreview(rows);
      setParseErrors(errs);
    } catch (e) { setParseErrors([String(e)]); }
    finally { setParsing(false); }
  };

  const upload = async () => {
    if (preview.length === 0) return toast.error('Nothing to upload');
    setUploading(true);
    try {
      const rows = preview.map(r => ({
        name: r.name, contact_person: r.contact_person || null, phone: r.phone || null,
        email: r.email || null, source: r.source || null, stage: r.stage || 'Cold',
        owner_name: r.owner_name || null, next_followup: r.next_followup || null,
        potential_value: r.potential_value ? Number(r.potential_value) : null,
        notes: r.notes || null,
      })).filter(r => r.name);
      const { data } = await axios.post(`${API}/business-dev/leads/bulk-import`, { rows }, { withCredentials: true });
      setResult(data);
      toast.success(`Imported ${data.created} of ${data.total_rows} leads`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk import failed');
    } finally { setUploading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f0f15] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle className="text-white">Bulk Import Leads</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-cyan-500/5 border border-cyan-500/20 rounded p-3 text-xs text-slate-300">
            <div className="font-semibold text-cyan-300 mb-1">How it works:</div>
            <ol className="list-decimal list-inside space-y-0.5 text-[11px]">
              <li>Download the template — required columns: <span className="text-white font-mono">name</span> (only mandatory); optional: contact_person, phone, email, source, stage, owner_name, next_followup, potential_value, notes</li>
              <li>Fill rows in Excel / Google Sheets, export as CSV</li>
              <li>Upload here — we'll validate and show a preview before committing</li>
            </ol>
          </div>
          <div className="flex gap-2">
            <Button onClick={downloadTemplate} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="download-template-btn">
              <Download size={14} className="mr-2" /> Download Template
            </Button>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold cursor-pointer" data-testid="csv-file-label">
              <Upload size={14} /> Choose CSV file
              <input type="file" accept=".csv" onChange={handleFile} className="hidden" data-testid="csv-file-input" />
            </label>
            {fileName && <span className="text-xs text-slate-400 self-center">{fileName}</span>}
          </div>
          {parsing && <div className="text-xs text-slate-400">Parsing...</div>}
          {parseErrors.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded p-2 text-[11px] text-amber-300" data-testid="parse-errors">
              {parseErrors.slice(0, 5).map((e, i) => <div key={i}>⚠ {e}</div>)}
              {parseErrors.length > 5 && <div>+ {parseErrors.length - 5} more...</div>}
            </div>
          )}
          {preview.length > 0 && !result && (
            <div data-testid="csv-preview">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Preview ({preview.length} rows)</div>
              <div className="bg-black/40 border border-white/10 rounded max-h-[200px] overflow-auto">
                <table className="data-table text-[10px]">
                  <thead><tr><th>Name</th><th>Contact</th><th>Phone</th><th>Stage</th><th>Owner</th></tr></thead>
                  <tbody>{preview.slice(0, 20).map((r, i) => (
                    <tr key={i}><td className="text-white">{r.name}</td><td>{r.contact_person}</td><td>{r.phone}</td><td>{r.stage}</td><td>{r.owner_name}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
          {result && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-3 text-xs text-emerald-200" data-testid="import-result">
              <div className="font-semibold">✓ Imported {result.created} of {result.total_rows} leads</div>
              {result.errors?.length > 0 && (
                <div className="mt-2 text-amber-300">
                  {result.errors.length} row(s) skipped:
                  <ul className="mt-1 list-disc list-inside text-[11px]">
                    {result.errors.slice(0, 5).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="ghost" className="text-slate-400" data-testid="bulk-close">Close</Button>
          {result ? (
            <Button onClick={onDone} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-done">Done</Button>
          ) : (
            <Button onClick={upload} disabled={preview.length === 0 || uploading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-upload-btn">
              {uploading ? 'Uploading...' : `Import ${preview.length} Lead${preview.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadCard({ lead, canWrite, canDelete, onClick, onEdit, onDelete }) {
  const today = new Date().toISOString().slice(0, 10);
  const overdue = lead.next_followup && lead.next_followup < today && !['Won', 'Lost'].includes(lead.stage);
  return (
    <div className="bg-black/40 border border-white/5 rounded-lg p-3 hover:border-white/10 transition-colors group" data-testid={`bd-lead-${lead.lead_id}`}>
      <div className="cursor-pointer" onClick={onClick}>
        <div className="flex items-start justify-between mb-1">
          <div className="text-sm text-white font-semibold truncate">{lead.name}</div>
        </div>
        {lead.contact_person && <div className="text-[11px] text-slate-400 truncate">{lead.contact_person}</div>}
        {lead.potential_value > 0 && (
          <div className="text-[10px] text-emerald-400 font-mono mt-1">{fmtINR(lead.potential_value)}</div>
        )}
        {lead.next_followup && (
          <div className={`text-[10px] mt-1 flex items-center gap-1 ${overdue ? 'text-rose-400' : 'text-slate-500'}`}>
            <Calendar size={10} /> {lead.next_followup} {overdue && '⚠'}
          </div>
        )}
        {lead.owner_name && <div className="text-[10px] text-slate-500 mt-1">@ {lead.owner_name}</div>}
      </div>
      <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {lead.phone && (
          <>
            <a href={`tel:${cleanPhone(lead.phone)}`} className="p-1.5 rounded bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20" title="Call" data-testid={`bd-call-${lead.lead_id}`} onClick={e => e.stopPropagation()}>
              <Phone size={11} />
            </a>
            <a href={whatsappUrl(lead.phone, `Hi ${lead.contact_person || ''}`.trim())} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20" title="WhatsApp" data-testid={`bd-whatsapp-${lead.lead_id}`} onClick={e => e.stopPropagation()}>
              <MessageCircle size={11} />
            </a>
          </>
        )}
        {lead.email && (
          <a href={`mailto:${lead.email}`} className="p-1.5 rounded bg-amber-500/10 text-amber-400 hover:bg-amber-500/20" title="Email" data-testid={`bd-email-${lead.lead_id}`} onClick={e => e.stopPropagation()}>
            <Mail size={11} />
          </a>
        )}
        <div className="flex-1" />
        {canWrite && (
          <button onClick={e => { e.stopPropagation(); onEdit(); }} className="p-1.5 rounded bg-white/5 text-slate-400 hover:text-white hover:bg-white/10" title="Edit" data-testid={`bd-edit-${lead.lead_id}`}>
            <Edit2 size={11} />
          </button>
        )}
        {canDelete && (
          <button onClick={e => { e.stopPropagation(); onDelete(); }} className="p-1.5 rounded bg-rose-500/10 text-rose-400 hover:bg-rose-500/20" title="Delete" data-testid={`bd-delete-${lead.lead_id}`}>
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

function LeadDialog({ mode, lead, employees, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: lead?.name || '',
    contact_person: lead?.contact_person || '',
    phone: lead?.phone || '',
    email: lead?.email || '',
    source: lead?.source || '',
    stage: lead?.stage || 'Cold',
    owner_id: lead?.owner_id || '',
    next_followup: lead?.next_followup || '',
    notes: lead?.notes || '',
    potential_value: lead?.potential_value || '',
  });
  const [saving, setSaving] = useState(false);
  const [sources, setSources] = useState([]);

  useEffect(() => {
    axios.get(`${API}/business-dev/meta`, { withCredentials: true })
      .then(r => setSources(r.data.sources || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Lead name is required');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        source: form.source || null,
        stage: form.stage,
        owner_id: form.owner_id || null,
        next_followup: form.next_followup || null,
        notes: form.notes.trim() || null,
        potential_value: form.potential_value ? Number(form.potential_value) : null,
      };
      if (mode === 'create') {
        await axios.post(`${API}/business-dev/leads`, payload, { withCredentials: true });
        toast.success('Lead created');
      } else {
        await axios.put(`${API}/business-dev/leads/${lead.lead_id}`, payload, { withCredentials: true });
        toast.success('Lead updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f0f15] border-white/10 max-w-xl" data-testid="lead-dialog">
        <DialogHeader><DialogTitle className="text-white">{mode === 'create' ? 'New Lead' : 'Edit Lead'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Company / Lead Name *</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-black/40 border-white/10 text-white" data-testid="lead-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Contact Person</label>
              <Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} className="bg-black/40 border-white/10 text-white" data-testid="lead-contact" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Phone (with country code)</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91..." className="bg-black/40 border-white/10 text-white" data-testid="lead-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Email</label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-black/40 border-white/10 text-white" data-testid="lead-email" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Source</label>
              <Select value={form.source} onValueChange={v => setForm({ ...form, source: v })}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="lead-source"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[300]">{sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Stage</label>
              <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="lead-stage"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[300]">{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Owner</label>
              <Select value={form.owner_id} onValueChange={v => setForm({ ...form, owner_id: v })}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="lead-owner"><SelectValue placeholder="Assign" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[300]">{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Next Follow-up</label>
              <Input type="date" value={form.next_followup} onChange={e => setForm({ ...form, next_followup: e.target.value })} className="bg-black/40 border-white/10 text-white" data-testid="lead-followup" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Potential Value (₹)</label>
            <Input type="number" value={form.potential_value} onChange={e => setForm({ ...form, potential_value: e.target.value })} className="bg-black/40 border-white/10 text-white" data-testid="lead-value" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm" data-testid="lead-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="ghost" className="text-slate-400" data-testid="lead-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="lead-submit">
            {saving ? 'Saving...' : (mode === 'create' ? 'Create Lead' : 'Update Lead')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadDetail({ lead, onClose, onChanged, canWrite }) {
  const [activities, setActivities] = useState(lead.activities || []);
  const [actType, setActType] = useState('call');
  const [actSummary, setActSummary] = useState('');
  const [logging, setLogging] = useState(false);

  const log = async () => {
    if (!actSummary.trim()) return toast.error('Summary required');
    setLogging(true);
    try {
      const { data } = await axios.post(`${API}/business-dev/leads/${lead.lead_id}/activities`,
        { type: actType, summary: actSummary.trim() }, { withCredentials: true });
      setActivities([...activities, data]);
      setActSummary('');
      toast.success('Activity logged');
      onChanged();
    } catch (e) {
      toast.error('Log failed');
    } finally { setLogging(false); }
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="bg-[#0a0a0f] border-white/10 text-white w-full sm:max-w-md overflow-y-auto" data-testid="lead-detail">
        <SheetHeader><SheetTitle className="text-white">{lead.name}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-3">
          <div className={`inline-block text-[11px] px-2 py-1 rounded border ${STAGE_COLORS[lead.stage]}`}>{lead.stage}</div>

          <div className="space-y-1 text-xs text-slate-300">
            {lead.contact_person && <div className="flex items-center gap-2"><User size={12} className="text-slate-500" /> {lead.contact_person}</div>}
            {lead.phone && <div className="flex items-center gap-2"><Phone size={12} className="text-slate-500" /> {lead.phone}</div>}
            {lead.email && <div className="flex items-center gap-2"><Mail size={12} className="text-slate-500" /> {lead.email}</div>}
            {lead.source && <div className="flex items-center gap-2"><Building2 size={12} className="text-slate-500" /> {lead.source}</div>}
            {lead.next_followup && <div className="flex items-center gap-2"><Calendar size={12} className="text-slate-500" /> Next: {lead.next_followup}</div>}
            {lead.owner_name && <div className="text-slate-500">Owner: {lead.owner_name}</div>}
            {lead.potential_value > 0 && <div className="text-emerald-400 font-mono">{fmtINR(lead.potential_value)}</div>}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            {lead.phone && (
              <>
                <a href={`tel:${cleanPhone(lead.phone)}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 text-xs font-semibold" data-testid="detail-call">
                  <Phone size={13} /> Call
                </a>
                <a href={whatsappUrl(lead.phone, `Hi ${lead.contact_person || ''}`.trim())} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-semibold" data-testid="detail-whatsapp">
                  <MessageCircle size={13} /> WhatsApp
                </a>
              </>
            )}
            {lead.email && (
              <a href={`mailto:${lead.email}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-xs font-semibold" data-testid="detail-email">
                <Mail size={13} /> Email
              </a>
            )}
          </div>

          {lead.notes && (
            <div className="bg-white/5 border border-white/5 rounded p-3 text-xs text-slate-300 whitespace-pre-wrap">{lead.notes}</div>
          )}

          {/* Activity log */}
          <div className="border-t border-white/5 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Activity Log</div>
            {canWrite && (
              <div className="flex gap-2 mb-3">
                <Select value={actType} onValueChange={setActType}>
                  <SelectTrigger className="w-[120px] bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="activity-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10 z-[300]">
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={actSummary} onChange={e => setActSummary(e.target.value)} placeholder="Summary"
                  className="bg-black/40 border-white/10 text-white h-9 text-xs flex-1" data-testid="activity-summary" />
                <Button onClick={log} disabled={logging} className="bg-indigo-600 hover:bg-indigo-700 h-9 text-xs" data-testid="activity-log-btn">Log</Button>
              </div>
            )}
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {activities.length === 0 ? (
                <div className="text-[11px] text-slate-600 text-center py-3">No activities yet</div>
              ) : [...activities].reverse().map((a, i) => (
                <div key={i} className="bg-black/30 rounded p-2 text-[11px]">
                  <div className="flex items-center justify-between text-slate-500"><span className="uppercase font-bold text-slate-400">{a.type}</span><span>{(a.logged_at || '').slice(0, 16).replace('T', ' ')}</span></div>
                  <div className="text-slate-200 mt-0.5">{a.summary}</div>
                  <div className="text-slate-600 text-[9px] mt-0.5">by {a.logged_by}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}


// =====================================================================
// Daily Leads Tab — Delhi-only auto-scraped prospects (Apify Google Maps)
// 6 statuses: new, contacted, follow_up, interested, not_interested, irrelevant
// =====================================================================

const DAILY_STATUS_COLORS = {
  new:            'bg-cyan-500/10 text-cyan-300 border-cyan-500/20',
  contacted:      'bg-amber-500/10 text-amber-300 border-amber-500/20',
  follow_up:      'bg-violet-500/10 text-violet-300 border-violet-500/20',
  interested:     'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  not_interested: 'bg-rose-500/10 text-rose-300 border-rose-500/20',
  irrelevant:     'bg-slate-500/10 text-slate-400 border-slate-500/20',
};

const DAILY_STATUS_OPTIONS = [
  { value: 'new',            label: 'New' },
  { value: 'contacted',      label: 'Contacted' },
  { value: 'follow_up',      label: 'Follow-up' },
  { value: 'interested',     label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'irrelevant',     label: 'Irrelevant' },
];

const STATUS_LABEL = Object.fromEntries(DAILY_STATUS_OPTIONS.map(o => [o.value, o.label]));

function DailyLeadsTab({ employees, isAdmin, canWrite, onMoved }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [scraperState, setScraperState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [fStatus, setFStatus] = useState('new');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fStatus !== 'all') params.status = fStatus;
      if (search.trim()) params.search = search.trim();
      const [lr, sr, ss] = await Promise.all([
        axios.get(`${API}/daily-leads`, { withCredentials: true, params }),
        axios.get(`${API}/daily-leads/stats`, { withCredentials: true }),
        axios.get(`${API}/daily-leads/scraper-state`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setLeads(lr.data);
      setStats(sr.data);
      setScraperState(ss.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fStatus, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runNow = async () => {
    if (!isAdmin) return;
    if (!window.confirm('Run scraper now? Uses Apify credits (~$1-2 per run for up to 100 Delhi leads).')) return;
    setRunning(true);
    toast.message('Scraping started — this may take 2-3 minutes...');
    try {
      const { data } = await axios.post(`${API}/daily-leads/run-now`, {}, { withCredentials: true, timeout: 420000 });
      toast.success(`Scraped ${data.scraped_total} · ${data.inserted} new leads added`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scrape failed — check APIFY_TOKEN');
    } finally { setRunning(false); }
  };

  const updateStatus = async (lead_hash, status) => {
    try {
      await axios.put(`${API}/daily-leads/${lead_hash}`, { status }, { withCredentials: true });
      toast.success(`Status → ${STATUS_LABEL[status]}`);
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const logOutreach = async (lead_hash, channel) => {
    try {
      await axios.post(`${API}/daily-leads/${lead_hash}/outreach`,
        { channel }, { withCredentials: true });
      toast.success('Marked as Contacted');
      fetchData();
    } catch { toast.error('Failed to log outreach'); }
  };

  const moveToBd = async (lead) => {
    if (!window.confirm(`Move "${lead.name}" to main BD pipeline?`)) return;
    try {
      const { data } = await axios.post(`${API}/daily-leads/${lead.lead_hash}/move-to-bd`,
        { stage: 'Contacted' }, { withCredentials: true });
      toast.success(`Moved → ${data.bd_lead_id}`);
      fetchData();
      onMoved && onMoved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Move failed'); }
  };

  const wipeAll = async () => {
    if (!isAdmin) return;
    const confirm1 = window.prompt(
      `This will permanently delete EVERY daily lead and reset the Delhi scraper back to pincode 110001.\n\nType WIPE to confirm:`
    );
    if (confirm1 !== 'WIPE') {
      if (confirm1 !== null) toast.error('Cancelled — confirmation did not match');
      return;
    }
    try {
      const { data } = await axios.delete(`${API}/daily-leads/wipe`, {
        params: { confirm: 'WIPE' },
        withCredentials: true,
      });
      toast.success(`Wiped ${data.leads_deleted} leads · scraper reset to ${data.scraper_reset_to}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wipe failed');
    }
  };

  return (
    <div data-testid="daily-leads-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="daily-kpis">
        <div className="glass-card p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">Total</div><div className="text-2xl font-heading font-black text-white">{stats?.total || 0}</div></div>
        <div className="glass-card p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">New</div><div className="text-2xl font-heading font-black text-cyan-400">{stats?.by_status?.new || 0}</div></div>
        <div className="glass-card p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">Contacted</div><div className="text-2xl font-heading font-black text-amber-400">{stats?.by_status?.contacted || 0}</div></div>
        <div className="glass-card p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">Follow-up</div><div className="text-2xl font-heading font-black text-violet-400">{stats?.by_status?.follow_up || 0}</div></div>
        <div className="glass-card p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">Interested</div><div className="text-2xl font-heading font-black text-emerald-400">{stats?.by_status?.interested || 0}</div></div>
      </div>

      {/* Delhi sequential progress strip */}
      {scraperState?.delhi && (
        <div className="glass-card p-3 mb-4 flex items-center justify-between" data-testid="delhi-progress">
          <div className="text-xs text-slate-300 flex items-center gap-2">
            <MapPin size={13} className="text-orange-400" />
            <span className="text-slate-500">Delhi · scraping pincode:</span>{' '}
            <span className="text-white font-mono font-bold">{scraperState.delhi.current_pincode}</span>
            <span className="text-slate-500 ml-1">({scraperState.delhi.collected_this_pincode || 0} of {scraperState.per_pincode_target} target)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-[140px] h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-400 to-amber-500"
                style={{ width: `${Math.min(100, ((scraperState.delhi.collected_this_pincode || 0) / scraperState.per_pincode_target) * 100)}%` }}></div>
            </div>
            <span className="text-[10px] text-slate-500">
              Pincode {(scraperState.delhi.current_pincode_index || 0) + 1} / {scraperState.total_pincodes}
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name/phone/area"
            className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs pl-8 w-[220px]" data-testid="daily-search" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-[160px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="daily-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
            <SelectItem value="all">All Status</SelectItem>
            {DAILY_STATUS_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {stats?.last_run && (
          <span className="text-[10px] text-slate-500" data-testid="daily-last-run">
            Last run: {stats.last_run.started_at?.slice(0, 16).replace('T', ' ')} · {stats.last_run.inserted} new
          </span>
        )}
        {isAdmin && (
          <Button onClick={wipeAll} variant="outline"
            className="bg-rose-500/10 border-rose-500/30 text-rose-300 hover:bg-rose-500/20 hover:text-rose-200 h-9 text-xs"
            data-testid="wipe-leads-btn">
            <Trash2 size={14} className="mr-1" /> Wipe All
          </Button>
        )}
        {isAdmin && (
          <Button onClick={runNow} disabled={running} className="bg-purple-600 hover:bg-purple-700 h-9 text-xs" data-testid="run-scrape-btn">
            <RefreshCw size={14} className={`mr-1 ${running ? 'animate-spin' : ''}`} /> {running ? 'Scraping...' : 'Run Now'}
          </Button>
        )}
      </div>

      {loading && leads.length === 0 ? (
        <div className="glass-card p-12 text-center text-slate-500">Loading...</div>
      ) : leads.length === 0 ? (
        <div className="glass-card p-12 text-center text-slate-500" data-testid="daily-empty">
          <Sparkles size={32} className="mx-auto mb-3 opacity-30" />
          <div className="text-sm">No leads yet. {isAdmin && 'Click "Run Now" to scrape your first batch (or wait for tomorrow\'s 8 AM auto-run).'}</div>
        </div>
      ) : (
        <div className="glass-card overflow-hidden" data-testid="daily-leads-table">
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead><tr>
                <th>Business</th><th>Category</th><th>Pincode</th>
                <th>Phone</th><th>Website</th><th>Status</th><th className="text-right">Actions</th>
              </tr></thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.lead_hash} className="hover:bg-white/[0.02]" data-testid={`daily-row-${lead.lead_hash}`}>
                    <td>
                      <div className="text-white font-semibold max-w-[180px] truncate" title={lead.name}>{lead.name}</div>
                      {lead.rating && <div className="text-[10px] text-amber-400">★ {lead.rating} ({lead.reviews_count || 0})</div>}
                    </td>
                    <td className="text-slate-400 max-w-[140px] truncate" title={lead.category}>{lead.category || '—'}</td>
                    <td className="text-slate-400 max-w-[120px] truncate" title={lead.area}>
                      <div className="flex items-center gap-1">
                        <MapPin size={10} className="text-orange-400" />
                        {lead.area}
                      </div>
                    </td>
                    <td className="font-mono text-[10px] text-slate-400">{lead.phone || '—'}</td>
                    <td className="max-w-[140px] truncate">
                      {lead.website ? <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline text-[10px]" onClick={e => e.stopPropagation()}>{lead.website.replace(/^https?:\/\//, '').slice(0, 25)}</a> : <span className="text-slate-600">—</span>}
                    </td>
                    <td>
                      <Select value={lead.status} onValueChange={(v) => updateStatus(lead.lead_hash, v)}>
                        <SelectTrigger
                          className={`h-7 text-[10px] px-2 w-[125px] border ${DAILY_STATUS_COLORS[lead.status] || 'border-white/10 bg-black/40 text-slate-300'}`}
                          data-testid={`daily-status-${lead.lead_hash}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                          {DAILY_STATUS_OPTIONS.map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        {lead.phone && (
                          <>
                            <a href={`tel:${cleanPhone(lead.phone)}`}
                              onClick={() => logOutreach(lead.lead_hash, 'call')}
                              className="p-1 rounded bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20" title="Call"
                              data-testid={`daily-call-${lead.lead_hash}`}>
                              <Phone size={11} />
                            </a>
                            <a href={whatsappUrl(lead.phone, lead.custom_message || `Hi! We help businesses like ${lead.name} grow with digital marketing — would you be open to a quick chat?`)}
                              target="_blank" rel="noopener noreferrer"
                              onClick={() => logOutreach(lead.lead_hash, 'whatsapp')}
                              className="p-1 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20" title="WhatsApp"
                              data-testid={`daily-whatsapp-${lead.lead_hash}`}>
                              <MessageCircle size={11} />
                            </a>
                          </>
                        )}
                        <a href={lead.email ? `mailto:${lead.email}` : '#'}
                          onClick={(e) => {
                            if (!lead.email) { e.preventDefault(); toast.message('No email on file — open detail to draft via AI'); return; }
                            logOutreach(lead.lead_hash, 'email');
                          }}
                          className={`p-1 rounded ${lead.email ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' : 'bg-white/5 text-slate-600 cursor-not-allowed'}`}
                          title={lead.email ? 'Email' : 'No email on file'}
                          data-testid={`daily-email-${lead.lead_hash}`}>
                          <Mail size={11} />
                        </a>
                        <button onClick={() => setSelected(lead)} className="p-1 rounded bg-white/5 text-slate-400 hover:text-white" title="Open"
                          data-testid={`daily-open-${lead.lead_hash}`}>
                          <Edit2 size={11} />
                        </button>
                        {canWrite && lead.status === 'interested' && !lead.moved_to_bd_lead_id && (
                          <button onClick={() => moveToBd(lead)}
                            className="px-2 py-1 rounded bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/30 inline-flex items-center gap-1 text-[10px] font-semibold"
                            title="Move to BD Tracker"
                            data-testid={`daily-move-${lead.lead_hash}`}>
                            <ArrowRightCircle size={11} /> Move to BD
                          </button>
                        )}
                        {lead.moved_to_bd_lead_id && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-300 border border-indigo-500/20" title={`Linked: ${lead.moved_to_bd_lead_id}`}>
                            In BD
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && <DailyLeadDetail lead={selected} onClose={() => setSelected(null)}
        onSaved={fetchData} onMoved={() => { fetchData(); onMoved && onMoved(); }}
        canWrite={canWrite}
        onStatusChange={(s) => updateStatus(selected.lead_hash, s)} />}
    </div>
  );
}

function DailyLeadDetail({ lead, onClose, onSaved, onMoved, canWrite, onStatusChange }) {
  const [custom, setCustom] = useState(lead.custom_message || '');
  const [notes, setNotes] = useState(lead.notes || '');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState({ whatsapp: false, email: false });

  const generate = async (channel) => {
    setGenerating(g => ({ ...g, [channel]: true }));
    try {
      const { data } = await axios.post(`${API}/daily-leads/${lead.lead_hash}/generate-message`,
        { channel }, { withCredentials: true, timeout: 60000 });
      if (channel === 'whatsapp') {
        setCustom(data.message || '');
        toast.success('WhatsApp message drafted');
      } else {
        setEmailSubject(data.subject || '');
        setEmailBody(data.message || '');
        toast.success('Email drafted');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(g => ({ ...g, [channel]: false }));
    }
  };

  const copyEmail = async () => {
    const text = emailSubject ? `Subject: ${emailSubject}\n\n${emailBody}` : emailBody;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Email copied to clipboard');
    } catch { toast.error('Copy failed'); }
  };

  const openMailto = () => {
    if (!lead.email && !lead.website) {
      toast.error('No email on file — copy and paste instead');
      return;
    }
    const params = new URLSearchParams();
    if (emailSubject) params.set('subject', emailSubject);
    if (emailBody) params.set('body', emailBody);
    window.open(`mailto:${lead.email || ''}?${params.toString()}`, '_blank');
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/daily-leads/${lead.lead_hash}`,
        { custom_message: custom, notes }, { withCredentials: true });
      toast.success('Saved');
      onSaved();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const move = async () => {
    try {
      const { data } = await axios.post(`${API}/daily-leads/${lead.lead_hash}/move-to-bd`,
        { stage: 'Contacted', notes }, { withCredentials: true });
      toast.success(`Moved → ${data.bd_lead_id}`);
      onMoved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Move failed'); }
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="bg-[#0a0a0f] border-white/10 text-white w-full sm:max-w-lg overflow-y-auto" data-testid="daily-detail">
        <SheetHeader><SheetTitle className="text-white">{lead.name}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-3 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block text-[11px] px-2 py-1 rounded border ${DAILY_STATUS_COLORS[lead.status]}`}>{STATUS_LABEL[lead.status] || lead.status}</span>
            {canWrite && (
              <Select value={lead.status} onValueChange={(v) => onStatusChange && onStatusChange(v)}>
                <SelectTrigger className="h-7 w-[150px] bg-black/40 border-white/10 text-slate-300 text-[10px]" data-testid="daily-detail-status">
                  <SelectValue placeholder="Change status..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                  {DAILY_STATUS_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {lead.moved_to_bd_lead_id && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                Linked: {lead.moved_to_bd_lead_id}
              </span>
            )}
          </div>
          <div className="space-y-1 text-slate-300">
            <div><span className="text-slate-500">Category:</span> {lead.category || '—'}</div>
            <div><span className="text-slate-500">Area:</span> {lead.area} · {lead.country}</div>
            {lead.phone && <div><span className="text-slate-500">Phone:</span> <span className="font-mono">{lead.phone}</span></div>}
            {lead.website && <div><span className="text-slate-500">Website:</span> <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">{lead.website}</a></div>}
            {lead.address && <div><span className="text-slate-500">Address:</span> {lead.address}</div>}
            {lead.rating && <div><span className="text-slate-500">Rating:</span> ★ {lead.rating} ({lead.reviews_count || 0} reviews)</div>}
            {lead.google_maps_url && <div><a href={lead.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">View on Google Maps →</a></div>}
          </div>

          {/* WhatsApp Message */}
          <div className="pt-2 border-t border-white/5">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <MessageCircle size={11} /> WhatsApp Message
              </label>
              <Button onClick={() => generate('whatsapp')} disabled={generating.whatsapp}
                size="sm" className="bg-purple-600 hover:bg-purple-700 h-6 text-[10px] px-2"
                data-testid="gen-whatsapp-btn">
                <Sparkles size={10} className="mr-1" /> {generating.whatsapp ? 'Drafting...' : 'AI Draft'}
              </Button>
            </div>
            <textarea value={custom} onChange={e => setCustom(e.target.value)} rows={5}
              placeholder={`Hi! We help businesses like ${lead.name} grow with digital marketing — would you be open to a quick chat?`}
              className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-xs font-mono"
              data-testid="custom-message" />
            {lead.phone && custom && (
              <a href={whatsappUrl(lead.phone, custom)} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-[11px] text-emerald-400 hover:text-emerald-300"
                data-testid="open-wa-with-msg">
                <MessageCircle size={11} /> Open WhatsApp with this message →
              </a>
            )}
          </div>

          {/* Email Message */}
          <div className="pt-2 border-t border-white/5">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <Mail size={11} /> Email
              </label>
              <Button onClick={() => generate('email')} disabled={generating.email}
                size="sm" className="bg-purple-600 hover:bg-purple-700 h-6 text-[10px] px-2"
                data-testid="gen-email-btn">
                <Sparkles size={10} className="mr-1" /> {generating.email ? 'Drafting...' : 'AI Draft'}
              </Button>
            </div>
            <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
              placeholder="Subject — drafted by AI"
              className="bg-black/40 border-white/10 text-white h-8 text-xs mb-1 font-semibold"
              data-testid="email-subject" />
            <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={6}
              placeholder="Click 'AI Draft' to generate a personalised email pitch..."
              className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-xs"
              data-testid="email-body" />
            {emailBody && (
              <div className="flex gap-2 mt-2">
                <button onClick={copyEmail} className="text-[11px] text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1" data-testid="copy-email">
                  <FileText size={11} /> Copy
                </button>
                <button onClick={openMailto} className="text-[11px] text-amber-400 hover:text-amber-300 inline-flex items-center gap-1" data-testid="open-mailto">
                  <Mail size={11} /> Open in Mail app
                </button>
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-white/5">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Internal Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-xs"
              data-testid="daily-notes" />
          </div>

          {(lead.outreach_log || []).length > 0 && (
            <div className="border-t border-white/5 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Outreach Log</div>
              {[...lead.outreach_log].reverse().map((e, i) => (
                <div key={i} className="bg-black/30 rounded p-2 text-[11px] mb-1">
                  <div className="flex items-center justify-between text-slate-500"><span className="uppercase font-bold text-slate-400">{e.channel}</span><span>{(e.at || '').slice(0, 16).replace('T', ' ')}</span></div>
                  {e.summary && <div className="text-slate-200 mt-0.5">{e.summary}</div>}
                  <div className="text-slate-600 text-[9px]">by {e.by}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 pt-3 border-t border-white/5">
            <Button onClick={save} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="daily-save">
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {canWrite && lead.status === 'interested' && !lead.moved_to_bd_lead_id && (
              <Button onClick={move} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="daily-move-btn">
                <ArrowRightCircle size={14} className="mr-1" /> Move to BD
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
